import { Injectable, signal, effect, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { StorageService } from './storage.service';

export type ThemeMode = 'light' | 'dark' | 'auto';

@Injectable({
  providedIn: 'root'
})
export class ThemeService {
  private platformId = inject(PLATFORM_ID);
  private storageService = inject(StorageService);
  private readonly THEME_KEY = 'theme-preference';

  theme = signal<ThemeMode>('light');
  isDark = signal<boolean>(false);

  constructor() {
    if (isPlatformBrowser(this.platformId)) {
      const savedTheme = this.storageService.getItem(this.THEME_KEY) as ThemeMode | null;
      if (savedTheme && ['light', 'dark', 'auto'].includes(savedTheme)) {
        this.theme.set(savedTheme);
      } else {
        this.theme.set('light');
      }

      this.updateTheme();

      effect(() => {
        const currentTheme = this.theme();
        this.updateTheme();
        this.storageService.setItem(this.THEME_KEY, currentTheme);
      });

      if (isPlatformBrowser(this.platformId)) {
        const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
        const handleChange = () => {
          if (this.theme() === 'auto') {
            this.updateTheme();
          }
        };
        mediaQuery.addEventListener('change', handleChange);
      }
    }
  }

  setTheme(theme: ThemeMode): void {
    this.theme.set(theme);
  }

  toggleTheme(): void {
    const current = this.theme();
    if (current === 'auto') {
      this.theme.set('dark');
    } else if (current === 'dark') {
      this.theme.set('light');
    } else {
      this.theme.set('auto');
    }
  }

  updateTheme(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const html = document.documentElement;
    const currentTheme = this.theme();

    html.classList.remove('dark');

    if (currentTheme === 'auto') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      this.isDark.set(prefersDark);
      if (prefersDark) {
        html.classList.add('dark');
      }
    } else if (currentTheme === 'dark') {
      this.isDark.set(true);
      html.classList.add('dark');
    } else {
      this.isDark.set(false);
    }
  }
}

